import React, {useState} from 'react';
import BottomSheetWebViewWrapper from '@di-shared/features/common/BottomSheetWebViewWrapper';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import PreventForm from '@shared/PreventForm';
import {getTireDisplayName} from './tirePressureUtilsEU';
import type {SelectedTireEU, TireInfo, TireRecommendedResponse} from './types';

interface TireSelectBottomSheetEUProps {
  vin: string;
  tireRecommendedData: TireRecommendedResponse;
}

/**
 * EU 버전 타이어 선택 바텀시트
 *
 * window.data를 통해 tireRecommendedData를 전달받아
 * API 호출 없이 바로 타이어 목록을 표시합니다.
 * 선택된 타이어를 {frontTire, rearTire} 형태로 반환하여 부모 컴포넌트에서 API를 호출합니다.
 */
function TireSelectBottomSheetEU({vin, tireRecommendedData}: TireSelectBottomSheetEUProps) {
  const {t} = useInferableTranslation();

  const {tires, registeredFrontTire, registeredRearTire} = tireRecommendedData;

  // 등록된 타이어를 찾아서 초기값 설정
  const initialSelectedTire: TireInfo | null =
    registeredFrontTire && registeredRearTire ? {frontTire: registeredFrontTire, rearTire: registeredRearTire} : null;

  const [selectedTire, setSelectedTire] = useState<TireInfo | null>(initialSelectedTire);

  /**
   * 타이어 선택 시 상태 업데이트
   */
  function selectTire(tire: TireInfo) {
    setSelectedTire(tire);
  }

  /**
   * 선택한 타이어를 부모 컴포넌트로 반환하고 바텀시트 닫기
   */
  function confirmTireSelection() {
    if (selectedTire) {
      const result: SelectedTireEU = {
        frontTire: selectedTire.frontTire,
        rearTire: selectedTire.rearTire,
      };
      window.bottomSheetClose(result);
    }
  }

  /**
   * 바텀시트 취소 및 닫기
   */
  function closeTireSelectionBottomSheet() {
    window.bottomSheetClose(null);
  }

  /**
   * 타이어 표시 형식 생성 (radio value용)
   * 앞뒤 타이어가 같으면: "245/45R20"
   * 앞뒤 타이어가 다르면: "245/45R20/245/45R20"
   */
  function getTireValue(frontTire: string, rearTire: string): string {
    return frontTire === rearTire ? frontTire : `${frontTire}/${rearTire}`;
  }

  /**
   * 현재 선택된 타이어인지 확인
   */
  function isTireSelected(tire: TireInfo): boolean {
    return selectedTire?.frontTire === tire.frontTire && selectedTire?.rearTire === tire.rearTire;
  }

  return (
    <BottomSheetWebViewWrapper bottomSheetFallbackHeight={300}>
      <div className="bottom_sheet">
        <div className="bottom_sheet_header">
          <h2 className="bottom_sheet_title">{t('msg_tire_select_tire_eu')}</h2>
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('ctn_home_close')}
            onClick={closeTireSelectionBottomSheet}
          >
            <span
              className="ic ic_close_20px"
              aria-hidden="true"
            ></span>
          </button>
        </div>
        <PreventForm onSubmit={confirmTireSelection}>
          <div className="bottom_sheet_inner">
            <div className="bottom_sheet_contents">
              <div className="tire_intro">
                <ImageSelector
                  className="tire_intro_img"
                  alt="tire_intro_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_select_intro_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_select_intro.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_select_intro_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_select_intro.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_select_intro_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_select_intro.png')}
                  fallback={
                    <div
                      className="tire_intro_img"
                      style={{height: 128}}
                    />
                  }
                />
                <p
                  className="tire_intro_desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('msg_tire_select_tire_checkinfo_eu')}
                </p>
              </div>
              <div className="box_outline_group">
                {tires?.map((tire) => {
                  const {frontTire, rearTire} = tire;
                  const value = getTireValue(frontTire, rearTire);
                  const label = getTireDisplayName(frontTire, rearTire, t);

                  return (
                    <div
                      className="input_box input_box_outline"
                      key={value}
                    >
                      <input
                        type="radio"
                        id={value}
                        name="tireSelectGroup"
                        value={value}
                        checked={isTireSelected(tire)}
                        className="input"
                        onChange={() => selectTire(tire)}
                      />
                      <label
                        htmlFor={value}
                        className="label"
                      >
                        {label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bottom_sheet_footer bottom_sheet_footer_complex">
              <button
                type="button"
                className="btn btn_large btn_outline"
                onClick={closeTireSelectionBottomSheet}
              >
                {t('ctn_tire_select_tire_calncel_eu')}
              </button>

              <button
                className="btn btn_large btn_filled"
                type={'submit'}
                disabled={!selectedTire}
              >
                {t('ctn_tire_select_tire_confirm_eu')}
              </button>
            </div>
          </div>
        </PreventForm>
      </div>
    </BottomSheetWebViewWrapper>
  );
}

export default TireSelectBottomSheetEU;
